import { useState, useCallback } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Save, RotateCcw, AlertCircle, Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { RichTextEditor } from '@/components/TemplateEditor'
import {
  getCommitmentLetterTemplate,
  updateCommitmentLetterTemplate,
} from '@/api/commitmentLetterTemplate'
import { queryKeys } from '@/lib/queryKeys'
import { toast } from '@/stores/useToastStore'
import type { AxiosError } from 'axios'

export default function AdminDocumentTemplates() {
  const queryClient = useQueryClient()
  const [editedContent, setEditedContent] = useState<string | null>(null)

  const {
    data: template,
    isLoading,
    isError,
  } = useQuery({
    queryKey: queryKeys.commitmentLetterTemplate.all,
    queryFn: getCommitmentLetterTemplate,
  })

  const { mutate: saveTemplate, isPending: isSaving } = useMutation({
    mutationFn: (content: string) =>
      updateCommitmentLetterTemplate({ content }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.commitmentLetterTemplate.all,
      })
      toast.success('Template saved successfully')
    },
    onError: (error: AxiosError<{ detail?: string }>) => {
      const message = error.response?.data?.detail ?? 'Failed to save template'
      toast.error(message)
    },
  })

  const isDirty = editedContent !== null

  const currentContent = editedContent ?? template?.content ?? ''

  const handleContentChange = useCallback(
    (content: string) => {
      if (template && content === template.content) {
        setEditedContent(null)
      } else {
        setEditedContent(content)
      }
    },
    [template]
  )

  const handleSave = useCallback(() => {
    if (editedContent !== null) {
      saveTemplate(editedContent)
    }
  }, [editedContent, saveTemplate])

  const handleRevert = useCallback(() => {
    setEditedContent(null)
    toast.info('Changes reverted')
  }, [])

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    )
  }

  if (isError || !template) {
    return (
      <div className="flex flex-col items-center justify-center gap-2 p-12 text-muted-foreground">
        <AlertCircle className="h-8 w-8" />
        <p>Failed to load template</p>
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-6 p-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-foreground">
            Commitment Letter Template
          </h1>
          <p className="mt-1 text-base text-muted-foreground">
            Edit the commitment letter template
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            onClick={handleRevert}
            disabled={!isDirty}
            className="gap-2"
          >
            <RotateCcw className="h-4 w-4" />
            Revert
          </Button>
          <Button
            onClick={handleSave}
            disabled={!isDirty || isSaving}
            className="gap-2"
          >
            {isSaving ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Save className="h-4 w-4" />
            )}
            Save
          </Button>
        </div>
      </div>

      {/* Editor Card */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-lg">Commitment Letter</CardTitle>
              <CardDescription>
                {template.updated_at
                  ? `Last modified: ${new Date(
                      template.updated_at
                    ).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                    })}`
                  : 'Never modified'}
              </CardDescription>
            </div>
            {isDirty && (
              <div className="flex items-center gap-1 text-sm text-amber-600">
                <AlertCircle className="h-4 w-4" />
                <span>Unsaved changes</span>
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent>
          <RichTextEditor
            content={currentContent}
            onChange={handleContentChange}
            templateId={template.slug}
          />
        </CardContent>
      </Card>
    </div>
  )
}
